import { RandInt } from "./plateGame.ts";
import { Coins } from "../../MathFactsGenerator/Constants.ts";
import { formatDollars } from "../../MathFactsGenerator/MoneyGenerator.ts";
import { formatRate, PERCENT_RATES } from "../../MathFactsGenerator/PercentGenerator.ts";

/*
 * Shop Rush (ADR 0131): a shift of ten customers, one order each. Right the first
 * time is 2 stars, right on the second try 1 star, wrong twice (or out of patience)
 * 0 stars and the worked answer. One level per grade, 2 to 8 (ADR 0131 §7): each
 * grade deals its own kinds of order - coins, change, totals, equal groups and
 * pizza fractions in the early grades; discounts, tax, tips, unit price, markup,
 * percent change and simple interest in the later ones.
 *
 * Pure, like runnerGame.ts and mergeGame.ts - no timers, no DOM, no React. Randomness
 * comes in as `randInt`, inclusive at both ends like `getRandomInt`, so tests can fix
 * it. Every function returns a new state and reads nothing but its arguments.
 *
 * Money is integer cents throughout. Nothing here rounds: a percent order draws its
 * rate first and then a price that makes price x rate a whole number of cents (ADR
 * 0131 §3, ADR 0120 §3), and every price is a whole number of dollars so a child never
 * meets a rounding convention. The builders reuse `PERCENT_RATES`, `formatRate`,
 * `formatDollars` (worksheets keep their columns comma-free; a sentence reads $1,400.00) and the US coin table, but not the worksheet builders: those write
 * English prompts through the global random generator.
 *
 * Text is not made here. A hint or a worked answer is a `ShopMessage`: an id and the
 * numbers (and already-formatted amounts) to put in it. `shopText.ts` turns it into the
 * player's language, so the engine has nothing to translate.
 */

export type ShopLevel = "g2" | "g3" | "g4" | "g5" | "g6" | "g7" | "g8";

export const SHOP_LEVELS: ShopLevel[] = ["g2", "g3", "g4", "g5", "g6", "g7", "g8"];

/** The grade a level stands for: "g4" is 4. */
export function gradeOf(level: ShopLevel): number {
    return Number(level.slice(1));
}

export type ShopPace = "calm" | "busy" | "rush";

export const SHOP_PACES: ShopPace[] = ["calm", "busy", "rush"];

/** How long each customer waits, by pace; Calm has no clock (ADR 0131 §2). */
export const PATIENCE_MS: Record<ShopPace, number | null> = { calm: null, busy: 60000, rush: 30000 };

/** Customers in a shift, and so the most orders there are to score. */
export const CUSTOMERS = 10;

export const STARS_PER_ORDER = 2;

export const MAX_STARS = CUSTOMERS * STARS_PER_ORDER;

/** The longest slice of time one tick counts, so a hidden tab or a long frame cannot cost a customer. */
export const MAX_TICK_MS = 250;

/** Different customer figures; `lookOf` picks one for each customer. */
export const LOOKS = 6;

export type ShopKind =
    | "coins"
    | "change"
    | "total"
    | "groups"
    | "fraction"
    | "discount"
    | "tax"
    | "tip"
    | "betterBuy"
    | "unitPrice"
    | "markup"
    | "discountTax"
    | "originalPrice"
    | "percentChange"
    | "interest";

/** What each grade deals (ADR 0131 §7), in about equal shares. */
export const SHOP_KINDS: Record<ShopLevel, ShopKind[]> = {
    g2: ["coins", "change", "total", "groups", "fraction"],
    g3: ["change", "total", "groups", "fraction"],
    g4: ["change", "total", "groups", "fraction"],
    g5: ["change", "total", "groups", "fraction"],
    g6: ["discount", "tip", "unitPrice", "betterBuy"],
    g7: ["discount", "tax", "tip", "markup"],
    g8: ["discountTax", "originalPrice", "percentChange", "interest"],
};

// ── The shop's goods ────────────────────────────────────────────────────────

/**
 * What the shop sells, with the range of prices that looks right for it, in cents. A
 * ring-up order draws each line's price from the part of this range the grade allows,
 * so a grade-2 sandwich is never 20 cents and a grade-5 one can cost $9.99.
 */
export const SHOP_ITEMS = [
    { id: "sticker", lo: 5, hi: 30 },
    { id: "pencil", lo: 10, hi: 50 },
    { id: "banana", lo: 15, hi: 60 },
    { id: "apple", lo: 20, hi: 95 },
    { id: "cookie", lo: 25, hi: 195 },
    { id: "juice", lo: 75, hi: 295 },
    { id: "milk", lo: 95, hi: 295 },
    { id: "bread", lo: 150, hi: 499 },
    { id: "sandwich", lo: 195, hi: 999 },
    { id: "notebook", lo: 150, hi: 599 },
] as const;

export type ShopItemId = (typeof SHOP_ITEMS)[number]["id"];

/** What an equal-groups order counts: a container and the things in it ("bags of rolls"). */
export const GROUP_WORDS = ["rolls", "crayons", "muffins", "apples", "stickers", "cookies", "oranges", "eggs"] as const;

export type GroupWord = (typeof GROUP_WORDS)[number];

/** A piece of money in the tray: a bill or a coin, with the diameter the coin table gives (mm) for drawing. */
export interface TrayPiece {
    cents: number;
    kind: "bill" | "coin";
    diameter?: number;
}

const coinOf = (name: string): TrayPiece => {
    const coin = Coins.find((c) => c.name === name)!;
    return { cents: coin.cents, kind: "coin", diameter: coin.diameter };
};

/** Bills largest first, then coins largest first, the order the tray draws them (ADR 0131 §3). */
export const TRAY_PIECES: TrayPiece[] = [
    { cents: 1000, kind: "bill" },
    { cents: 500, kind: "bill" },
    { cents: 100, kind: "bill" },
    coinOf("quarter"),
    coinOf("dime"),
    coinOf("nickel"),
    coinOf("penny"),
];

/** The coins a count-the-coins order is made of, largest first. */
export const COIN_VALUES = [25, 10, 5, 1];

/**
 * The tray for a change order: only pieces worth less than the amount paid (ADR 0131
 * §7). Change from $1.00 is coins only, from $5 adds $1 bills, from $10 adds $5 and
 * from $20 adds $10 - a bill as big as the one paid is never part of the change.
 */
export function trayPiecesFor(paidCents: number): TrayPiece[] {
    return TRAY_PIECES.filter((piece) => piece.cents < paidCents);
}

/** The coin piece (for its drawing size) behind a value in `COIN_VALUES`. */
export function coinPiece(cents: number): TrayPiece {
    return TRAY_PIECES.find((piece) => piece.kind === "coin" && piece.cents === cents)!;
}

// ── Orders ──────────────────────────────────────────────────────────────────

export interface CoinsOrder {
    kind: "coins";
    /** The coins the customer pays with, largest first. */
    coins: number[];
    totalCents: number;
}

export interface ChangeOrder {
    kind: "change";
    priceCents: number;
    paidCents: number;
    changeCents: number;
}

export interface TotalLine {
    id: ShopItemId;
    cents: number;
    qty: number;
}

export interface TotalOrder {
    kind: "total";
    items: TotalLine[];
    totalCents: number;
}

export interface GroupsOrder {
    kind: "groups";
    groups: number;
    each: number;
    word: GroupWord;
    answer: number;
}

export interface FractionOrder {
    kind: "fraction";
    num: number;
    den: number;
    slices: number;
    answer: number;
}

/** `rateTenths` is the rate in tenths of a percent (65 is 6.5%), as `PERCENT_RATES` holds it. */
export interface DiscountOrder {
    kind: "discount";
    rateTenths: number;
    priceCents: number;
    offCents: number;
    saleCents: number;
    ask: "sale" | "off";
}

export interface TaxOrder {
    kind: "tax";
    rateTenths: number;
    priceCents: number;
    taxCents: number;
    totalCents: number;
}

export interface TipOrder {
    kind: "tip";
    rateTenths: number;
    billCents: number;
    tipCents: number;
}

export interface Offer {
    count: number;
    cents: number;
}

export interface BetterBuyOrder {
    kind: "betterBuy";
    a: Offer;
    b: Offer;
    better: "a" | "b";
    /** Whole cents each, for offer a and offer b. */
    unitA: number;
    unitB: number;
}

export interface UnitPriceOrder {
    kind: "unitPrice";
    item: ShopItemId;
    count: number;
    totalCents: number;
    /** Whole cents for one. */
    unitCents: number;
}

export interface MarkupOrder {
    kind: "markup";
    rateTenths: number;
    costCents: number;
    markupCents: number;
    priceCents: number;
}

export interface DiscountTaxOrder {
    kind: "discountTax";
    discountTenths: number;
    taxTenths: number;
    priceCents: number;
    offCents: number;
    saleCents: number;
    taxCents: number;
    totalCents: number;
}

export interface OriginalPriceOrder {
    kind: "originalPrice";
    rateTenths: number;
    originalCents: number;
    offCents: number;
    saleCents: number;
}

export interface PercentChangeOrder {
    kind: "percentChange";
    direction: "up" | "down";
    fromCents: number;
    toCents: number;
    /** The whole-number percent the price changed by, the answer. */
    percent: number;
}

export interface InterestOrder {
    kind: "interest";
    principalCents: number;
    /** Whole percent a year. */
    rate: number;
    years: number;
    yearlyCents: number;
    interestCents: number;
}

export type ShopOrder =
    | CoinsOrder
    | ChangeOrder
    | TotalOrder
    | GroupsOrder
    | FractionOrder
    | DiscountOrder
    | TaxOrder
    | TipOrder
    | BetterBuyOrder
    | UnitPriceOrder
    | MarkupOrder
    | DiscountTaxOrder
    | OriginalPriceOrder
    | PercentChangeOrder
    | InterestOrder;

/** What a player gives for an order: cents or a whole number, or which offer. */
export type ShopAnswer = number | "a" | "b";

function pick<T>(items: readonly T[], randInt: RandInt): T {
    return items[randInt(0, items.length - 1)];
}

function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
}

function lcm(a: number, b: number): number {
    return (a / gcd(a, b)) * b;
}

/** Fisher-Yates through `randInt`. */
function shuffled<T>(items: readonly T[], randInt: RandInt): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = randInt(0, i);
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}

/** A multiple of `step` between `lo` and `hi` inclusive (both rounded in). */
function stepped(lo: number, hi: number, step: number, randInt: RandInt): number {
    return step * randInt(Math.ceil(lo / step), Math.floor(hi / step));
}

// ── Grades 2-5: coins, change, totals, groups and fractions ─────────────────

/**
 * Grade 2: 3 to 8 coins of 25, 10, 5 and 1 cent, no more than $1.00 between them. Each
 * coin is drawn only from the values that still leave room for the coins after it, at
 * a cent apiece, so the cap holds without a retry.
 */
export function coinsOrder(randInt: RandInt): CoinsOrder {
    const n = randInt(3, 8);
    const coins: number[] = [];
    let sum = 0;
    for (let i = 0; i < n; i++) {
        const room = 100 - sum - (n - i - 1);
        const value = pick(
            COIN_VALUES.filter((v) => v <= room),
            randInt
        );
        coins.push(value);
        sum += value;
    }
    coins.sort((x, y) => y - x);
    return { kind: "coins", coins, totalCents: sum };
}

/**
 * Make change (ADR 0131 §7). Grade 2: from $1.00, for 5 to 95 cents in 5s. Grade 3:
 * from $1, $2 or $5, for 25 cents to $4.95 in 5s. Grade 4: from the next dollar up, $5
 * or $10, for 25 cents to $9.99. Grade 5: from $10 or $20, for $1.00 to $19.99. In
 * grades 4 and 5 three prices in four are a multiple of 5 cents, so most counting-up
 * ends on a nickel.
 */
export function changeOrder(level: ShopLevel, randInt: RandInt): ChangeOrder {
    let priceCents: number;
    let paidCents: number;
    const mixedCents = (lo: number, hi: number) => (randInt(1, 4) === 1 ? randInt(lo, hi) : stepped(lo, hi, 5, randInt));
    switch (level) {
        case "g2":
            priceCents = stepped(5, 95, 5, randInt);
            paidCents = 100;
            break;
        case "g3":
            priceCents = stepped(25, 495, 5, randInt);
            paidCents = pick(
                [100, 200, 500].filter((paid) => paid > priceCents),
                randInt
            );
            break;
        case "g4": {
            priceCents = mixedCents(25, 999);
            const next = Math.floor(priceCents / 100) * 100 + 100;
            paidCents = pick(
                [...new Set([next, 500, 1000])].filter((paid) => paid >= next),
                randInt
            );
            break;
        }
        default:
            priceCents = mixedCents(100, 1999);
            paidCents = pick(
                [1000, 2000].filter((paid) => paid > priceCents),
                randInt
            );
    }
    return { kind: "change", priceCents, paidCents, changeCents: paidCents - priceCents };
}

interface TotalRule {
    lines: [number, number];
    qty: [number, number];
    maxCents: number;
    step: number;
}

/** Ring-up orders by grade (ADR 0131 §7): how many lines, how many of each, the dearest line, and the price step. */
const TOTAL_RULES: Record<string, TotalRule> = {
    g2: { lines: [2, 2], qty: [1, 1], maxCents: 50, step: 5 },
    g3: { lines: [2, 2], qty: [1, 2], maxCents: 295, step: 5 },
    g4: { lines: [2, 3], qty: [1, 3], maxCents: 495, step: 5 },
    g5: { lines: [2, 3], qty: [1, 4], maxCents: 999, step: 1 },
};

/** The ring-up rule for a grade; grades without one (6 to 8 do not ring up) use grade 5's. */
export function totalRuleOf(level: ShopLevel): TotalRule {
    return TOTAL_RULES[level] ?? TOTAL_RULES.g5;
}

/** Ring-up: different items from the shop, each priced inside its own range and the grade's. */
export function totalOrder(level: ShopLevel, randInt: RandInt): TotalOrder {
    const rule = totalRuleOf(level);
    const eligible = SHOP_ITEMS.filter((item) => item.lo <= rule.maxCents);
    const items: TotalLine[] = shuffled(eligible, randInt)
        .slice(0, randInt(rule.lines[0], rule.lines[1]))
        .map((item) => ({
            id: item.id,
            cents: stepped(item.lo, Math.min(item.hi, rule.maxCents), rule.step, randInt),
            qty: randInt(rule.qty[0], rule.qty[1]),
        }));
    return { kind: "total", items, totalCents: items.reduce((sum, line) => sum + line.qty * line.cents, 0) };
}

/** Equal groups by grade (ADR 0131 §7): the range of the number of groups and of the number in each. */
const GROUP_RULES: Record<string, { groups: [number, number]; each: [number, number] }> = {
    g2: { groups: [2, 5], each: [2, 5] },
    g3: { groups: [2, 10], each: [2, 10] },
    g4: { groups: [2, 9], each: [11, 50] },
    g5: { groups: [11, 25], each: [11, 30] },
};

export function groupsOrder(level: ShopLevel, randInt: RandInt): GroupsOrder {
    const rule = GROUP_RULES[level] ?? GROUP_RULES.g5;
    const groups = randInt(rule.groups[0], rule.groups[1]);
    const each = randInt(rule.each[0], rule.each[1]);
    return { kind: "groups", groups, each, word: pick(GROUP_WORDS, randInt), answer: groups * each };
}

/**
 * A fraction of a pizza (ADR 0131 §7). Grade 2: a unit fraction (1/2, 1/3, 1/4) of a
 * pizza cut into the denominator times 1, 2 or 3 slices, so the answer is 1, 2 or 3. Grade 3: a/b of a pizza cut into
 * b slices, b = 2, 3, 4, 6 or 8. Grade 4: slices a multiple of b (at least two a part),
 * up to 12. Grade 5: b up to 12 and up to 24 slices. The fraction is in lowest terms
 * and under 1, so the answer is a whole number of slices.
 */
export function fractionOrder(level: ShopLevel, randInt: RandInt): FractionOrder {
    const dens: Record<string, number[]> = {
        g2: [2, 3, 4],
        g3: [2, 3, 4, 6, 8],
        g4: [2, 3, 4, 5, 6],
        g5: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    };
    const maxSlices = level === "g4" ? 12 : 24;
    const den = pick(dens[level] ?? dens.g5, randInt);
    const nums: number[] = [];
    for (let n = 1; n < den; n++) {
        if (gcd(n, den) === 1 && (level !== "g2" || n === 1)) {
            nums.push(n);
        }
    }
    const num = pick(nums, randInt);
    let slices: number;
    if (level === "g2") {
        slices = den * randInt(1, 3);
    } else if (level === "g3") {
        slices = den;
    } else {
        slices = den * randInt(2, Math.floor(maxSlices / den));
    }
    return { kind: "fraction", num, den, slices, answer: (slices / den) * num };
}

// ── Grades 6-8: percent and money in context ────────────────────────────────

/**
 * The prices a rate keeps exact, in cents: a multiple of this makes price x rate a
 * whole number of cents and a whole number of dollars. Every rate in the three
 * lists comes to 100 or 200 - half-percent rates need an even number of dollars.
 */
export function priceStepCents(rateTenths: number): number {
    return lcm(1000 / gcd(rateTenths, 1000), 100);
}

/** A price in whole dollars between `fromDollars` and `toDollars` that the rate keeps exact. */
function exactPrice(rateTenths: number, fromDollars: number, toDollars: number, randInt: RandInt): number {
    const step = priceStepCents(rateTenths);
    const first = Math.ceil((fromDollars * 100) / step);
    const last = Math.max(first, Math.floor((toDollars * 100) / step));
    return step * randInt(first, last);
}

/** Cents of `rateTenths` of a tenth of a percent of `cents`. Exact for any price `exactPrice` returns. */
function percentOf(cents: number, rateTenths: number): number {
    return (cents * rateTenths) / 1000;
}

/** Grade 6 takes off 10, 20, 25 or 50%; grade 7 uses ADR 0120's discount rates. */
const GRADE_6_DISCOUNTS = [100, 200, 250, 500];

/** Grade 6 tips 10, 15 or 20%; grade 7 uses ADR 0120's tip rates. */
const GRADE_6_TIPS = [100, 150, 200];

export function discountOrder(level: ShopLevel, randInt: RandInt): DiscountOrder {
    const grade6 = level === "g6";
    const rateTenths = pick(grade6 ? GRADE_6_DISCOUNTS : PERCENT_RATES.discount, randInt);
    const priceCents = exactPrice(rateTenths, 8, grade6 ? 100 : 120, randInt);
    const offCents = percentOf(priceCents, rateTenths);
    // "How much is taken off?" now and then, instead of the sale price.
    const ask = randInt(1, 3) === 1 ? "off" : "sale";
    return { kind: "discount", rateTenths, priceCents, offCents, saleCents: priceCents - offCents, ask };
}

export function taxOrder(randInt: RandInt): TaxOrder {
    const rateTenths = pick(PERCENT_RATES.tax, randInt);
    const priceCents = exactPrice(rateTenths, 6, 100, randInt);
    const taxCents = percentOf(priceCents, rateTenths);
    return { kind: "tax", rateTenths, priceCents, taxCents, totalCents: priceCents + taxCents };
}

export function tipOrder(level: ShopLevel, randInt: RandInt): TipOrder {
    const grade6 = level === "g6";
    const rateTenths = pick(grade6 ? GRADE_6_TIPS : PERCENT_RATES.tip, randInt);
    const billCents = exactPrice(rateTenths, 10, grade6 ? 100 : 120, randInt);
    return { kind: "tip", rateTenths, billCents, tipCents: percentOf(billCents, rateTenths) };
}

/**
 * Better buy: two offers of different counts (2 to 12) with whole-cent
 * unit prices at least 2 cents apart. Whether the larger count is the cheaper each is
 * decided first, a coin toss, so the bigger pack is the better buy about half the time.
 */
export function betterBuyOrder(randInt: RandInt): BetterBuyOrder {
    const small = randInt(2, 11);
    const large = randInt(small + 1, 12);
    const largerWins = randInt(0, 1) === 1;
    const base = randInt(30, 200);
    const unitLow = base;
    const unitHigh = base + randInt(2, 15);
    const unitLarge = largerWins ? unitLow : unitHigh;
    const unitSmall = largerWins ? unitHigh : unitLow;
    const largeFirst = randInt(0, 1) === 1;
    const offer = (count: number, unit: number): Offer => ({ count, cents: count * unit });
    const first = largeFirst ? offer(large, unitLarge) : offer(small, unitSmall);
    const second = largeFirst ? offer(small, unitSmall) : offer(large, unitLarge);
    const unitA = first.cents / first.count;
    const unitB = second.cents / second.count;
    return { kind: "betterBuy", a: first, b: second, better: unitA < unitB ? "a" : "b", unitA, unitB };
}

/** Grade 6 unit price: 2 to 12 of an item from the shop, one costing a whole number of cents (10 cents up, inside the item's range). */
export function unitPriceOrder(randInt: RandInt): UnitPriceOrder {
    const item = pick(SHOP_ITEMS, randInt);
    const count = randInt(2, 12);
    const unitCents = randInt(Math.max(item.lo, 10), Math.min(item.hi, 500));
    return { kind: "unitPrice", item: item.id, count, totalCents: count * unitCents, unitCents };
}

/** Grade 7 markups: 10% to 100% in steps of 5, in tenths of a percent. */
export const MARKUP_RATES = Array.from({ length: 19 }, (_, i) => 100 + 50 * i);

/** Grade 7 markup: a cost in whole dollars, so every rate comes to a whole number of cents. */
export function markupOrder(randInt: RandInt): MarkupOrder {
    const rateTenths = pick(MARKUP_RATES, randInt);
    const costCents = 100 * randInt(4, 80);
    const markupCents = percentOf(costCents, rateTenths);
    return { kind: "markup", rateTenths, costCents, markupCents, priceCents: costCents + markupCents };
}

/**
 * The whole-dollar prices, $20 to $200 in cents, for which a discount and then a tax
 * both come to a whole number of cents. Every discount rate in the game is exact on a
 * whole dollar; it is the tax on the sale price that rules some prices out.
 */
export function exactDiscountTaxPrices(discountTenths: number, taxTenths: number): number[] {
    const prices: number[] = [];
    for (let dollars = 20; dollars <= 200; dollars++) {
        const priceCents = dollars * 100;
        const saleCents = priceCents - percentOf(priceCents, discountTenths);
        if ((priceCents * discountTenths) % 1000 === 0 && (saleCents * taxTenths) % 1000 === 0) {
            prices.push(priceCents);
        }
    }
    return prices;
}

/** Grade 8: a discount, then tax on the sale price. The two rates come first, then a price both steps keep exact. */
export function discountTaxOrder(randInt: RandInt): DiscountTaxOrder {
    let discountTenths = 250;
    let taxTenths = 80;
    let prices = exactDiscountTaxPrices(discountTenths, taxTenths);
    for (let attempt = 0; attempt < 20; attempt++) {
        const d = pick(PERCENT_RATES.discount, randInt);
        const t = pick(PERCENT_RATES.tax, randInt);
        const candidates = exactDiscountTaxPrices(d, t);
        if (candidates.length > 0) {
            discountTenths = d;
            taxTenths = t;
            prices = candidates;
            break;
        }
    }
    const priceCents = pick(prices, randInt);
    const offCents = percentOf(priceCents, discountTenths);
    const saleCents = priceCents - offCents;
    const taxCents = percentOf(saleCents, taxTenths);
    return {
        kind: "discountTax",
        discountTenths,
        taxTenths,
        priceCents,
        offCents,
        saleCents,
        taxCents,
        totalCents: saleCents + taxCents,
    };
}

/**
 * The discounts an original-price order uses: the multiples of 10%, so that what is
 * paid is a whole number of tenths of the price ("80% is $48.00, so 10% is $6.00").
 */
export const ORIGINAL_PRICE_RATES = [100, 200, 300, 400, 500, 600, 700];

/** Grade 8: the original price comes first, in whole dollars, so the sale price is exact cents. */
export function originalPriceOrder(randInt: RandInt): OriginalPriceOrder {
    const rateTenths = pick(ORIGINAL_PRICE_RATES, randInt);
    const originalCents = 100 * randInt(10, 150);
    const offCents = percentOf(originalCents, rateTenths);
    return { kind: "originalPrice", rateTenths, originalCents, offCents, saleCents: originalCents - offCents };
}

/** Grade 8: a price that rose by 5 to 100% or fell by 5 to 75% (steps of 5), from whole dollars, so the percent is a whole number. */
export function percentChangeOrder(randInt: RandInt): PercentChangeOrder {
    const direction = randInt(0, 1) === 1 ? "up" : "down";
    const percent = stepped(5, direction === "up" ? 100 : 75, 5, randInt);
    const fromCents = 100 * randInt(10, 100);
    const delta = (fromCents * percent) / 100;
    return {
        kind: "percentChange",
        direction,
        fromCents,
        toCents: direction === "up" ? fromCents + delta : fromCents - delta,
        percent,
    };
}

/** Grade 8: simple interest on $100 to $2,000 (steps of $50) at 1 to 10% for 1 to 5 years, in exact cents. */
export function interestOrder(randInt: RandInt): InterestOrder {
    const principalCents = 5000 * randInt(2, 40);
    const rate = randInt(1, 10);
    const years = randInt(1, 5);
    const yearlyCents = (principalCents * rate) / 100;
    return { kind: "interest", principalCents, rate, years, yearlyCents, interestCents: yearlyCents * years };
}

export function buildOrder(kind: ShopKind, level: ShopLevel, randInt: RandInt): ShopOrder {
    switch (kind) {
        case "coins":
            return coinsOrder(randInt);
        case "change":
            return changeOrder(level, randInt);
        case "total":
            return totalOrder(level, randInt);
        case "groups":
            return groupsOrder(level, randInt);
        case "fraction":
            return fractionOrder(level, randInt);
        case "discount":
            return discountOrder(level, randInt);
        case "tax":
            return taxOrder(randInt);
        case "tip":
            return tipOrder(level, randInt);
        case "betterBuy":
            return betterBuyOrder(randInt);
        case "unitPrice":
            return unitPriceOrder(randInt);
        case "markup":
            return markupOrder(randInt);
        case "discountTax":
            return discountTaxOrder(randInt);
        case "originalPrice":
            return originalPriceOrder(randInt);
        case "percentChange":
            return percentChangeOrder(randInt);
        default:
            return interestOrder(randInt);
    }
}

/** The answer an order wants: cents (change, money), a whole number or percent, or the better offer. */
export function answerOf(order: ShopOrder): ShopAnswer {
    switch (order.kind) {
        case "coins":
            return order.totalCents;
        case "change":
            return order.changeCents;
        case "total":
            return order.totalCents;
        case "groups":
        case "fraction":
            return order.answer;
        case "discount":
            return order.ask === "sale" ? order.saleCents : order.offCents;
        case "tax":
            return order.totalCents;
        case "tip":
            return order.tipCents;
        case "betterBuy":
            return order.better;
        case "unitPrice":
            return order.unitCents;
        case "markup":
            return order.priceCents;
        case "discountTax":
            return order.totalCents;
        case "originalPrice":
            return order.originalCents;
        case "percentChange":
            return order.percent;
        default:
            return order.interestCents;
    }
}

/** How the player gives an answer: coins and bills, a money keypad, a whole-number keypad, a percent keypad, or a tap on an offer. */
export type InputMode = "tray" | "money" | "number" | "percent" | "choice";

export function inputModeOf(order: ShopOrder): InputMode {
    switch (order.kind) {
        case "change":
            return "tray";
        case "groups":
        case "fraction":
            return "number";
        case "percentChange":
            return "percent";
        case "betterBuy":
            return "choice";
        default:
            return "money";
    }
}

// ── Money and the keypad ────────────────────────────────────────────────────

/** 65 -> "65¢", 365 -> "$3.65", 140000 -> "$1,400.00": under a dollar an amount reads as cents (ADR 0131 §3), from $1,000 it has the comma. */
export function money(cents: number): string {
    return cents > 0 && cents < 100 ? `${cents}¢` : formatDollars(cents);
}

/** Money fills in from the cents, so six digits make at most $9,999.99 (simple interest can reach $1,000.00). */
const MAX_MONEY_DIGITS = 6;
const MAX_NUMBER_DIGITS = 3;

export type KeypadMode = "money" | "number" | "percent";

/**
 * The text after a key press on a till keypad. Money works like a cash register
 * (ADR 0131 §3): the text is the digits, which are the amount in cents, so 7 then 5 is
 * 75 cents and 2 1 0 0 is $21.00. A leading zero adds nothing and is not kept, and a
 * seventh digit is not taken. A whole number or a percent is plain digits, up to three,
 * with a leading zero replaced. No decimal point is taken in any mode; anything else
 * leaves the text alone.
 */
export function keypadAppend(text: string, key: string, mode: KeypadMode): string {
    if (!/^[0-9]$/.test(key)) {
        return text;
    }
    const base = text === "" || text === "0" ? "" : text;
    if (mode !== "money") {
        return base.length >= MAX_NUMBER_DIGITS ? text : base + key;
    }
    if (base.length >= MAX_MONEY_DIGITS) {
        return text;
    }
    // A zero on an empty display adds nothing, so $0.00 stays an empty entry.
    return base + key === "0" ? "" : base + key;
}

export function keypadBackspace(text: string): string {
    return text.slice(0, -1);
}

/**
 * The cents, whole number or percent a keypad's text stands for, or null when nothing
 * has been typed (or, for money, only zeros): serving $0.00 or an empty display is not
 * an answer, so it is not a wrong try.
 */
export function parseEntry(text: string, mode: KeypadMode): number | null {
    if (!/[0-9]/.test(text)) {
        return null;
    }
    const value = parseInt(text, 10);
    return mode === "money" && value === 0 ? null : value;
}

/** What a money keypad shows for its text: "" is $0.00, "75" is 75 cents, "2100" is $21.00. */
export function moneyEntryText(text: string): string {
    return formatDollars(text === "" ? 0 : parseInt(text, 10));
}

/** What a percent keypad shows for its text: "" is 0%, "15" is 15%. */
export function percentEntryText(text: string): string {
    return `${text === "" ? "0" : text}%`;
}

// ── Counting up ─────────────────────────────────────────────────────────────

/**
 * The amounts a child says when counting up from the price to what was paid
 * ($3.65 -> $3.75 -> $4.00 -> $5.00), always starting at the price, ending at the
 * amount paid and strictly rising. From an odd cent it goes to the next 5; between
 * there and a quarter it steps by 10 until a quarter is no more than 10 away; from a
 * quarter it goes to the next half or dollar; and from a dollar to the next of $5,
 * $10, $20, or the amount paid, whichever comes first.
 */
export function countUpSteps(priceCents: number, paidCents: number): number[] {
    const steps = [priceCents];
    let cur = priceCents;
    while (cur < paidCents) {
        const from = cur;
        let next: number;
        if (from % 100 === 0) {
            next = [500, 1000, 2000].find((landmark) => landmark > from && landmark <= paidCents) ?? paidCents;
        } else if (cur % 5 !== 0) {
            next = cur + (5 - (cur % 5));
        } else if (cur % 25 === 0) {
            next = cur + (50 - (cur % 50));
        } else {
            const toQuarter = 25 - (cur % 25);
            next = toQuarter <= 10 ? cur + toQuarter : cur - (cur % 10) + 10;
        }
        cur = Math.min(next, paidCents);
        steps.push(cur);
    }
    return steps;
}

// ── Hints and worked answers ────────────────────────────────────────────────

export type ShopMessageId =
    | "hintCoins"
    | "hintChange"
    | "hintTotal"
    | "hintTotalCents"
    | "hintGroups"
    | "hintFraction"
    | "hintFractionSplit"
    | "hintFractionSlice"
    | "hintPercentOf"
    | "hintTenthBuild"
    | "hintTenthDivide"
    | "hintTenth"
    | "hintQuarter"
    | "hintTip15"
    | "hintTip18"
    | "hintBetterBuy"
    | "hintUnitPrice"
    | "hintDiscountTax"
    | "hintOriginalPrice"
    | "hintPercentChange"
    | "hintInterest"
    | "workedCoins"
    | "workedChange"
    | "workedTotal"
    | "workedGroups"
    | "workedFraction"
    | "workedDiscountSale"
    | "workedDiscountOff"
    | "workedTax"
    | "workedTip10"
    | "workedTip15"
    | "workedTip18"
    | "workedTip20"
    | "workedTip25"
    | "workedBetterBuy"
    | "workedUnitPrice"
    | "workedDiscountTax"
    | "workedOriginalPrice"
    | "workedPercentChange"
    | "workedInterest"
    | "workedInterestOne";

/** A sentence for the player: its id, and the figures to put in it, already written out. */
export interface ShopMessage {
    id: ShopMessageId;
    values: Record<string, string | number>;
}

const message = (id: ShopMessageId, values: Record<string, string | number> = {}): ShopMessage => ({ id, values });

/** The fraction as one glyph (1/2 -> ½) where there is one; the rest read "2/5". */
const FRACTION_GLYPHS: Record<string, string> = {
    "1/2": "½",
    "1/3": "⅓",
    "2/3": "⅔",
    "1/4": "¼",
    "3/4": "¾",
    "1/6": "⅙",
    "5/6": "⅚",
    "1/8": "⅛",
    "3/8": "⅜",
    "5/8": "⅝",
    "7/8": "⅞",
};

export function fractionText(num: number, den: number): string {
    return FRACTION_GLYPHS[`${num}/${den}`] ?? `${num}/${den}`;
}

/** A hint points at the method and never gives the answer (ADR 0131 §4, §7). */
export function hintFor(order: ShopOrder): ShopMessage {
    switch (order.kind) {
        case "coins":
            return message("hintCoins");
        case "change":
            return message("hintChange", { price: money(order.priceCents), paid: money(order.paidCents) });
        case "total":
            // "Dollars, then cents" is no help when every price is under a dollar.
            return order.items.every((line) => line.cents < 100) ? message("hintTotalCents") : message("hintTotal");
        case "groups":
            return message("hintGroups", { groups: order.groups, each: order.each });
        case "fraction": {
            // With one part wanted, the size of a part is the answer, so the hint stops at the split.
            if (order.num === 1) {
                return message("hintFractionSplit", { slices: order.slices, den: order.den });
            }
            // One slice to a part: the split is the answer's whole story, so point at the slice instead.
            return order.slices === order.den
                ? message("hintFractionSlice", { den: order.den, fraction: fractionText(order.num, order.den) })
                : message("hintFraction", { slices: order.slices, den: order.den, per: order.slices / order.den });
        }
        case "discount": {
            const rate = formatRate(order.rateTenths);
            const price = money(order.priceCents);
            if (order.ask === "sale") {
                return message("hintPercentOf", { rate, price });
            }
            return order.rateTenths === 100
                ? message("hintTenthDivide", { price })
                : message("hintTenthBuild", { rate, price, tenth: money(order.priceCents / 10) });
        }
        case "tax":
            return message("hintPercentOf", { rate: formatRate(order.rateTenths), price: money(order.priceCents) });
        case "tip": {
            const price = money(order.billCents);
            const tenth = money(order.billCents / 10);
            switch (order.rateTenths) {
                case 100:
                    return message("hintTenthDivide", { price });
                case 150:
                    return message("hintTip15", { price, tenth });
                case 180:
                    return message("hintTip18", { price, tenth });
                case 250:
                    return message("hintQuarter", { price });
                default:
                    return message("hintTenth", { price, tenth });
            }
        }
        case "betterBuy":
            return message("hintBetterBuy");
        case "unitPrice":
            return message("hintUnitPrice", { total: money(order.totalCents), count: order.count });
        case "markup":
            return message("hintPercentOf", { rate: formatRate(order.rateTenths), price: money(order.costCents) });
        case "discountTax":
            return message("hintDiscountTax");
        case "originalPrice":
            return message("hintOriginalPrice", {
                rate: formatRate(order.rateTenths),
                sale: money(order.saleCents),
                rest: 100 - order.rateTenths / 10,
            });
        case "percentChange":
            return message("hintPercentChange");
        default:
            // A single year's interest is the answer itself, so the hint there is the plain "find the percent".
            return order.years === 1
                ? message("hintPercentOf", { rate: order.rate, price: money(order.principalCents) })
                : message("hintInterest", { rate: order.rate, principal: money(order.principalCents) });
    }
}

/** The worked answer shown after a second miss or when patience runs out (ADR 0131 §4, §7). */
export function workedAnswerFor(order: ShopOrder): ShopMessage {
    switch (order.kind) {
        case "coins":
            return message("workedCoins", {
                terms: order.coins.map(money).join(" + "),
                total: money(order.totalCents),
            });
        case "change":
            return message("workedChange", {
                steps: countUpSteps(order.priceCents, order.paidCents).map(money).join(" → "),
                change: money(order.changeCents),
            });
        case "total":
            return message("workedTotal", {
                terms: order.items.flatMap((line) => Array<string>(line.qty).fill(money(line.cents))).join(" + "),
                total: money(order.totalCents),
            });
        case "groups":
            return message("workedGroups", { groups: order.groups, each: order.each, answer: order.answer });
        case "fraction":
            return message("workedFraction", {
                fraction: fractionText(order.num, order.den),
                slices: order.slices,
                den: order.den,
                per: order.slices / order.den,
                num: order.num,
                answer: order.answer,
            });
        case "discount": {
            const values = {
                rate: formatRate(order.rateTenths),
                price: money(order.priceCents),
                off: money(order.offCents),
                sale: money(order.saleCents),
            };
            return message(order.ask === "sale" ? "workedDiscountSale" : "workedDiscountOff", values);
        }
        case "tax":
            return message("workedTax", {
                rate: formatRate(order.rateTenths),
                price: money(order.priceCents),
                tax: money(order.taxCents),
                total: money(order.totalCents),
            });
        case "tip": {
            const bill = money(order.billCents);
            const tip = money(order.tipCents);
            const tenth = money(order.billCents / 10);
            switch (order.rateTenths) {
                case 100:
                    return message("workedTip10", { bill, tip });
                case 150:
                    return message("workedTip15", { tenth, half: money(order.billCents / 20), tip });
                case 180:
                    return message("workedTip18", {
                        twenty: money(order.billCents / 5),
                        two: money(order.billCents / 50),
                        tip,
                    });
                case 250:
                    return message("workedTip25", { bill, tip });
                default:
                    return message("workedTip20", { tenth, tip });
            }
        }
        case "betterBuy": {
            const winner = order[order.better];
            return message("workedBetterBuy", {
                priceA: money(order.a.cents),
                countA: order.a.count,
                unitA: money(order.unitA),
                priceB: money(order.b.cents),
                countB: order.b.count,
                unitB: money(order.unitB),
                winnerCount: winner.count,
                winnerPrice: money(winner.cents),
            });
        }
        case "unitPrice":
            return message("workedUnitPrice", {
                total: money(order.totalCents),
                count: order.count,
                unit: money(order.unitCents),
            });
        case "markup":
            // Worked as tax is: the markup is found, then added to the cost.
            return message("workedTax", {
                rate: formatRate(order.rateTenths),
                price: money(order.costCents),
                tax: money(order.markupCents),
                total: money(order.priceCents),
            });
        case "discountTax":
            return message("workedDiscountTax", {
                discount: formatRate(order.discountTenths),
                price: money(order.priceCents),
                off: money(order.offCents),
                sale: money(order.saleCents),
                tax: formatRate(order.taxTenths),
                taxAmount: money(order.taxCents),
                total: money(order.totalCents),
            });
        case "originalPrice": {
            const rest = 100 - order.rateTenths / 10;
            return message("workedOriginalPrice", {
                rest,
                sale: money(order.saleCents),
                tenth: money(order.saleCents / (rest / 10)),
                original: money(order.originalCents),
            });
        }
        case "percentChange":
            return message("workedPercentChange", {
                big: money(Math.max(order.fromCents, order.toCents)),
                small: money(Math.min(order.fromCents, order.toCents)),
                diff: money(Math.abs(order.toCents - order.fromCents)),
                from: money(order.fromCents),
                ratio: String(order.percent / 100),
                percent: order.percent,
            });
        default:
            return order.years === 1
                ? message("workedInterestOne", {
                      rate: order.rate,
                      principal: money(order.principalCents),
                      interest: money(order.interestCents),
                  })
                : message("workedInterest", {
                      rate: order.rate,
                      principal: money(order.principalCents),
                      yearly: money(order.yearlyCents),
                      years: order.years,
                      interest: money(order.interestCents),
                  });
    }
}

// ── The shift ───────────────────────────────────────────────────────────────

export type ShopPhase = "order" | "hint" | "served" | "left" | "over";

/** What the last move did, for the line the screen says. */
export type ShopEvent =
    | { kind: "arrive" }
    | { kind: "served"; stars: 1 | 2 }
    | { kind: "miss" }
    | { kind: "left"; reason: "wrong" | "patience" };

export interface ShopState {
    level: ShopLevel;
    pace: ShopPace;
    /** The kind of each customer's order, decided up front so a shift is balanced. */
    plan: ShopKind[];
    /** Which customer is at the counter, from 1. */
    customer: number;
    order: ShopOrder;
    /** Wrong tries on this order: 0, 1 or 2. */
    tries: number;
    phase: ShopPhase;
    /** Patience left in ms, or null on Calm. */
    patienceMs: number | null;
    stars: number;
    /** Orders served, on the first try or the second. */
    served: number;
    wrongTries: number;
    /** Customers who ran out of patience. */
    walkedOut: number;
    lookOffset: number;
    lastEvent: ShopEvent;
}

export interface ShopResult {
    level: ShopLevel;
    pace: ShopPace;
    stars: number;
    ordersServed: number;
    wrongTries: number;
    walkedOut: number;
}

export function resultOf(state: ShopState): ShopResult {
    return {
        level: state.level,
        pace: state.pace,
        stars: state.stars,
        ordersServed: state.served,
        wrongTries: state.wrongTries,
        walkedOut: state.walkedOut,
    };
}

/** The kinds of a shift: each of the grade's kinds twice and the rest drawn from them, in a shuffled order (about equal shares). */
export function planShift(level: ShopLevel, randInt: RandInt): ShopKind[] {
    const kinds = SHOP_KINDS[level];
    const extra = shuffled(kinds, randInt).slice(0, Math.max(0, CUSTOMERS - 2 * kinds.length));
    return shuffled([...kinds, ...kinds, ...extra], randInt).slice(0, CUSTOMERS);
}

export function startShift(level: ShopLevel, pace: ShopPace, randInt: RandInt): ShopState {
    const plan = planShift(level, randInt);
    return {
        level,
        pace,
        plan,
        customer: 1,
        order: buildOrder(plan[0], level, randInt),
        tries: 0,
        phase: "order",
        patienceMs: PATIENCE_MS[pace],
        stars: 0,
        served: 0,
        wrongTries: 0,
        walkedOut: 0,
        lookOffset: randInt(0, LOOKS - 1),
        lastEvent: { kind: "arrive" },
    };
}

/** Which of the `LOOKS` figures the customer at the counter is. */
export function lookOf(state: ShopState): number {
    return (state.lookOffset + state.customer - 1) % LOOKS;
}

/** The share of patience left, 0 to 1; null on Calm. */
export function patienceLeft(state: ShopState): number | null {
    const full = PATIENCE_MS[state.pace];
    return full === null || state.patienceMs === null ? null : state.patienceMs / full;
}

/** Is the clock running? Only while a customer is waiting for an answer, on Busy or Rush. */
export function clockRuns(state: ShopState): boolean {
    return state.patienceMs !== null && (state.phase === "order" || state.phase === "hint");
}

/**
 * Takes `dtMs` off the waiting customer's patience. The step is clamped to
 * MAX_TICK_MS and a negative one counts nothing. At nothing left the customer
 * walks out: no stars, the worked answer, one more walk-out.
 */
export function tick(state: ShopState, dtMs: number): ShopState {
    if (!clockRuns(state)) {
        return state;
    }
    const step = Math.min(Math.max(dtMs, 0), MAX_TICK_MS);
    const patienceMs = Math.max(0, state.patienceMs! - step);
    if (patienceMs > 0) {
        return { ...state, patienceMs };
    }
    return {
        ...state,
        patienceMs: 0,
        phase: "left",
        walkedOut: state.walkedOut + 1,
        lastEvent: { kind: "left", reason: "patience" },
    };
}

/**
 * Serves the order with `value`. Right is 2 stars the first time and 1 the second.
 * The first miss is a hint and a second try; the second miss and the customer leaves.
 * Does nothing unless a customer is waiting for an answer.
 */
export function answer(state: ShopState, value: ShopAnswer): ShopState {
    if (state.phase !== "order" && state.phase !== "hint") {
        return state;
    }
    if (value === answerOf(state.order)) {
        const stars = state.tries === 0 ? 2 : 1;
        return {
            ...state,
            phase: "served",
            stars: state.stars + stars,
            served: state.served + 1,
            lastEvent: { kind: "served", stars },
        };
    }
    const tries = state.tries + 1;
    const wrongTries = state.wrongTries + 1;
    if (tries >= 2) {
        return { ...state, tries, wrongTries, phase: "left", lastEvent: { kind: "left", reason: "wrong" } };
    }
    return { ...state, tries, wrongTries, phase: "hint", lastEvent: { kind: "miss" } };
}

/** The next customer, or the end of the shift after the tenth. Does nothing while this one is still being served. */
export function nextCustomer(state: ShopState, randInt: RandInt): ShopState {
    if (state.phase !== "served" && state.phase !== "left") {
        return state;
    }
    if (state.customer >= CUSTOMERS) {
        return { ...state, phase: "over" };
    }
    return {
        ...state,
        customer: state.customer + 1,
        order: buildOrder(state.plan[state.customer], state.level, randInt),
        tries: 0,
        phase: "order",
        patienceMs: PATIENCE_MS[state.pace],
        lastEvent: { kind: "arrive" },
    };
}
